import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Cog,
} from "lucide-react";

import { EmptyState } from "@/components/empty-state";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ErrorMessage } from "@/components/ui/error-message";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import type { PeriodicTask, PeriodicTaskHealth } from "@/lib/types";

/* The API exposes raw scheduler rows (dotted task paths, cron syntax).
   This card translates them for HR/admin eyes: module groupings,
   sentence-case job names, plain-English schedules, and a single
   health rollup. Technical identifiers stay reachable via tooltip. */

const GROUP_LABELS: Record<string, string> = {
  accounts: "Accounts & security",
  attendance: "Attendance",
  biometrics: "Biometrics",
  celery: "System maintenance",
  core: "System maintenance",
  compensation: "Compensation",
  employees: "Employees",
  expenses: "Expenses",
  leave: "Leave",
  lifecycle: "Lifecycle",
  notifications: "Notifications",
  payroll: "Payroll",
  performance: "Performance",
  recruitment: "Recruitment",
  reports: "Reports",
  workflows: "Approvals & workflows",
};

function groupLabel(task: PeriodicTask): string {
  const prefix = task.name.split(".")[0] ?? "";
  return (
    GROUP_LABELS[prefix] ??
    (prefix ? prefix.charAt(0).toUpperCase() + prefix.slice(1) : "Other")
  );
}

/** "accounts.auto-unlock-expired-lockouts" → "Auto unlock expired lockouts" */
function jobLabel(task: PeriodicTask): string {
  const slug = task.name.includes(".")
    ? task.name.slice(task.name.indexOf(".") + 1)
    : task.name;
  const words = slug.replace(/[-_]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : task.name;
}

const HEALTH_DOT: Record<PeriodicTaskHealth, string> = {
  healthy: "bg-[#10B981]",
  overdue: "bg-[#F59E0B]",
  paused: "bg-slate-300",
  pending: "bg-[#3B82F6]",
};

function HealthDot({ health }: { health: PeriodicTaskHealth }) {
  return (
    <span
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${HEALTH_DOT[health]}`}
      aria-hidden="true"
    />
  );
}

function lastRunPhrase(task: PeriodicTask): string {
  if (task.health === "paused") return "paused";
  if (task.health === "pending") return "waiting for first run";
  return `last ran ${formatRelative(task.last_run_at)}`;
}

function JobRow({ task }: { task: PeriodicTask }) {
  return (
    <li
      className="flex items-center gap-2.5 py-1.5"
      title={`${task.name}\n${task.task}\n${task.total_run_count.toLocaleString()} run${task.total_run_count === 1 ? "" : "s"}${task.description ? `\n${task.description}` : ""}`}
    >
      <HealthDot health={task.health} />
      <span className="min-w-0 flex-1 truncate text-sm text-navy">
        {jobLabel(task)}
      </span>
      <span className="whitespace-nowrap text-xs text-slate">
        {task.schedule_display} · {lastRunPhrase(task)}
      </span>
    </li>
  );
}

export function BeatStatusCard() {
  const [showAll, setShowAll] = useState(false);
  const tasks = useQuery({
    queryKey: ["periodic-tasks"],
    queryFn: () => api.get<PeriodicTask[]>("/core/periodic-tasks/"),
    refetchInterval: 60_000,
  });

  // The endpoint returns a plain array; guard against paginated/object
  // payloads (and test mocks) so a shape mismatch degrades to "empty".
  const data = Array.isArray(tasks.data) ? tasks.data : [];
  const overdue = data.filter((t) => t.health === "overdue");
  const paused = data.filter((t) => t.health === "paused");
  const pending = data.filter((t) => t.health === "pending");
  const healthy = data.filter((t) => t.health === "healthy");
  const allHealthy = overdue.length === 0;

  const groups = new Map<string, PeriodicTask[]>();
  for (const t of data) {
    const label = groupLabel(t);
    const bucket = groups.get(label);
    if (bucket) bucket.push(t);
    else groups.set(label, [t]);
  }
  const sortedGroups = [...groups.entries()].sort(([a], [b]) =>
    a.localeCompare(b),
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Background jobs</CardTitle>
            <CardDescription>
              Automated schedules that keep attendance, payroll, and
              notifications up to date.
            </CardDescription>
          </div>
          {tasks.isFetching && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-soft px-2 py-0.5 text-[10px] font-medium text-slate">
              <Clock size={10} aria-hidden="true" />
              refreshing
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {tasks.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        )}
        {tasks.isError && (
          <ErrorMessage
            error={tasks.error}
            fallback="Couldn't load background jobs."
          />
        )}
        {tasks.isSuccess && data.length === 0 && (
          <EmptyState
            icon={Cog}
            title="No background jobs scheduled"
            description="Run `make seed` or register one via /admin/django_celery_beat/periodictask/."
          />
        )}
        {tasks.isSuccess && data.length > 0 && (
          <div className="space-y-3">
            {/* Health rollup — the one line most users need. */}
            <div
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                allHealthy
                  ? "bg-[#10B981]/10 text-[#047857]"
                  : "bg-[#F59E0B]/10 text-[#B45309]"
              }`}
            >
              {allHealthy ? (
                <CheckCircle2 size={16} strokeWidth={1.5} aria-hidden="true" />
              ) : (
                <AlertTriangle size={16} strokeWidth={1.5} aria-hidden="true" />
              )}
              <span className="flex-1 font-medium">
                {allHealthy
                  ? `All ${healthy.length + pending.length} active jobs are running on schedule`
                  : `${overdue.length} job${overdue.length === 1 ? " hasn't" : "s haven't"} run on schedule`}
              </span>
              <span className="text-xs font-normal opacity-80">
                {[
                  `${healthy.length} on schedule`,
                  pending.length > 0 && `${pending.length} awaiting first run`,
                  paused.length > 0 && `${paused.length} paused`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>

            {/* Overdue jobs surface unconditionally. */}
            {overdue.length > 0 && (
              <ul className="divide-y divide-slate-100 rounded-lg border border-[#F59E0B]/30 px-3 py-1">
                {overdue.map((t) => (
                  <JobRow key={t.id} task={t} />
                ))}
              </ul>
            )}

            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate hover:text-navy"
            >
              {showAll ? (
                <ChevronUp size={14} aria-hidden="true" />
              ) : (
                <ChevronDown size={14} aria-hidden="true" />
              )}
              {showAll ? "Hide job list" : `Show all ${data.length} jobs`}
            </button>

            {showAll && (
              <div className="space-y-4">
                {sortedGroups.map(([label, groupTasks]) => (
                  <div key={label}>
                    <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate">
                      {label}
                    </h4>
                    <ul className="divide-y divide-slate-100">
                      {groupTasks.map((t) => (
                        <JobRow key={t.id} task={t} />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
